import { useContext } from 'react';
import { AppModal, SimpleGrid, Flex } from '../ui';
import { addInputs } from '../../helpers/math/spadesMath';
import { PlayerInput } from '../forms';
import { TeamInputHeading } from '../forms';
import { GlobalContext } from '../../helpers/context';
import type { Round, Names, ModalOpenArgs } from '../../types';

export interface ErrorModalProps {
  isOpen: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
  index?: number;
  names: Names;
  isCurrent: boolean;
  roundHistory: Round[];
  currentRound?: Round;
  errorMessage: string;
  onOpenModal?: (args: ModalOpenArgs) => void;
}

function ErrorModal({
  isOpen,
  setIsModalOpen,
  index,
  names,
  isCurrent,
  roundHistory,
  currentRound: propCurrentRound, // Rename to avoid confusion
  errorMessage,
  onOpenModal,
}: ErrorModalProps) {
  const { currentRound: contextCurrentRound } = useContext(GlobalContext);

  const currentRound = propCurrentRound || contextCurrentRound;

  const { team1BidsAndActuals, team2BidsAndActuals } = currentRound;
  const team1Actuals = {
    p1Actual: team1BidsAndActuals?.p1Actual ?? '',
    p2Actual: team1BidsAndActuals?.p2Actual ?? '',
  };
  const team2Actuals = {
    p1Actual: team2BidsAndActuals?.p1Actual ?? '',
    p2Actual: team2BidsAndActuals?.p2Actual ?? '',
  };
  const team1ActualTotal = addInputs(
    team1Actuals.p1Actual,
    team1Actuals.p2Actual,
  );
  const team2ActualTotal = addInputs(
    team2Actuals.p1Actual,
    team2Actuals.p2Actual,
  );

  // Check if any actuals are auto-generated using the new tracking
  const hasAutoGeneratedActuals = () => {
    if (currentRound && currentRound.autoGeneratedActuals) {
      return Object.values(currentRound.autoGeneratedActuals).some(
        (value) => value === true,
      );
    }
    return false;
  };

  const showAutoGeneratedKey = hasAutoGeneratedActuals();

  return (
    <AppModal
      isOpen={isOpen}
      onClose={setIsModalOpen}
      showCloseButton={false}
      closeOnInteractOutside={false}
      closeOnEscape={false}
    >
      <div
        data-cy="errorModalActualSection"
        data-testid="actualSection"
        style={{ paddingTop: '20px' }}
      >
        <TeamInputHeading
          team1Total={team1ActualTotal}
          team2Total={team2ActualTotal}
          title="Actuals"
          team1Bids={[team1BidsAndActuals?.p1Bid, team1BidsAndActuals?.p2Bid]}
          team2Bids={[team2BidsAndActuals?.p1Bid, team2BidsAndActuals?.p2Bid]}
          isEditable={true}
          index={index}
          isCurrent={isCurrent}
          currentRound={currentRound}
          roundHistory={roundHistory}
          onOpenModal={onOpenModal}
        />
        <Flex
          color="errorRed"
          direction="row"
          justify="center"
          p="var(--app-spacing-5)"
        >
          {errorMessage}
        </Flex>
        <SimpleGrid
          columns={2}
          className="namesContainer"
          gap={2}
          style={{ marginTop: '5px', marginBottom: '20px' }}
        >
          <PlayerInput
            roundHistory={roundHistory}
            teamName={names.team1Name}
            index={index as number}
            isCurrent={isCurrent}
            type={'Actual'}
            playerName={names.t1p1Name}
            currentRound={currentRound}
            inputId="team1BidsAndActuals.p1Actual"
            dealerId="team1BidsAndActuals.p1Bid"
            fieldToUpdate={'team1BidsAndActuals.p1Actual'}
            playerInput={team1BidsAndActuals?.p1Actual}
            teamClassName="team1"
            onOpenModal={onOpenModal}
          />
          <PlayerInput
            roundHistory={roundHistory}
            teamName={names.team2Name}
            index={index as number}
            isCurrent={isCurrent}
            type={'Actual'}
            playerName={names.t2p1Name}
            currentRound={currentRound}
            inputId="team2BidsAndActuals.p1Actual"
            dealerId="team2BidsAndActuals.p1Bid"
            fieldToUpdate={'team2BidsAndActuals.p1Actual'}
            playerInput={team2BidsAndActuals?.p1Actual}
            teamClassName="team2"
            onOpenModal={onOpenModal}
          />
          <PlayerInput
            roundHistory={roundHistory}
            teamName={names.team1Name}
            index={index as number}
            isCurrent={isCurrent}
            type={'Actual'}
            playerName={names.t1p2Name}
            currentRound={currentRound}
            inputId="team1BidsAndActuals.p2Actual"
            dealerId="team1BidsAndActuals.p2Bid"
            fieldToUpdate={'team1BidsAndActuals.p2Actual'}
            playerInput={team1BidsAndActuals?.p2Actual}
            teamClassName="team1"
            onOpenModal={onOpenModal}
          />
          <PlayerInput
            roundHistory={roundHistory}
            teamName={names.team2Name}
            index={index as number}
            isCurrent={isCurrent}
            type={'Actual'}
            playerName={names.t2p2Name}
            currentRound={currentRound}
            inputId="team2BidsAndActuals.p2Actual"
            dealerId="team2BidsAndActuals.p2Bid"
            fieldToUpdate={'team2BidsAndActuals.p2Actual'}
            playerInput={team2BidsAndActuals?.p2Actual}
            teamClassName="team2"
            onOpenModal={onOpenModal}
          />
        </SimpleGrid>
        {showAutoGeneratedKey && (
          <div
            style={{
              marginTop: '10px',
              textAlign: 'center',
              fontSize: '0.8em',
              color: 'gray',
              marginBottom: '10px',
            }}
          >
            * auto-generated
          </div>
        )}
      </div>
    </AppModal>
  );
}

export default ErrorModal;
